/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Seo, Footer } from '@components/systems'
import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'
import {
	findSoftwareBySlug,
	SoftwarePlatformType,
	SoftwareType,
} from '@src/configs/software.config'
import { SEO_SITE } from '@src/configs/seo.config'
import { Analytics, buildBreadcrumbJsonLd, buildSoftwareApplicationJsonLd } from '@utils'

import studioAudioImg from '@public/images/monolith/studio-audio.jpg'
import studioPresetsImg from '@public/images/monolith/studio-switch-presets.jpg'
import studioEditorImg from '@public/images/monolith/studio-editor.jpg'
import studioPedalsImg from '@public/images/monolith/studio-pedals.jpg'
import studioGlobalImg from '@public/images/monolith/studio-global.jpg'
import studioTrsImg from '@public/images/monolith/studio-trs.jpg'
import studioDictImg from '@public/images/monolith/studio-dict.jpg'

import { useNavigate, useParams } from 'react-router-dom'
import NotFoundPage from '@pages/home/NotFound.page'

/**
 * 코드 → 상세 스크린샷. [0] 이 리드샷이고 나머지가 On screen 으로 간다.
 *
 * 전부 같은 규격이다 — 창째로 잡아 1600x990 (타이틀바 포함). 크기가 섞이면 목록에서
 * 행 높이가 들쭉날쭉해진다 (2026-09-14 사용자 지시).
 */
const shotsObj: { [code: string]: { src: string; caption: string }[] } = {
	studio: [
		{ src: studioPresetsImg, caption: 'Switch presets: what every footswitch does.' },
		{ src: studioEditorImg, caption: 'Banks, songs and setlists.' },
		{ src: studioAudioImg, caption: 'Loops, buffer, tuner and the chain.' },
		{ src: studioPedalsImg, caption: 'Every pedal on the board, and how it connects.' },
		{ src: studioGlobalImg, caption: 'Global tempo, clock presets and pages.' },
		{ src: studioTrsImg, caption: 'The TRS jacks, set per socket.' },
		{ src: studioDictImg, caption: 'The MIDI dictionary, by manufacturer and product.' },
	],
}

/** `studio/shell/latest.json` 에서 이 페이지가 읽는 부분. 나머지 필드는 표시용이 아니다 */
interface InstallerPointerType {
	/** 앱 버전이 아니라 **경로 토큰**(`r1`, `r2` …). 화면에 내지 않는다 */
	version?: unknown
	platforms?: unknown
}

/**
 * `token` 은 설치파일의 경로 토큰이고 `fileObj` 는 플랫폼 id → 파일명이다. 둘 다 포인터에서 온다.
 * 버전은 상태에 두지 않는다 — 화면에 적지 않기로 했다 (`software.config.ts` `latestUrl` 주석).
 */
type LatestStateType =
	| { status: 'loading' }
	| { status: 'ready'; token: string; fileObj: { [platformId: string]: string } }
	| { status: 'unavailable' }

/** 포인터에서 받을 수 있는 것만 추린다. 형태가 어긋나면 `unavailable` */
const readPointer = (json: InstallerPointerType): LatestStateType => {
	const token = typeof json.version === 'string' ? json.version : ''
	const fileObj: { [platformId: string]: string } = {}

	if (token !== '' && Array.isArray(json.platforms)) {
		json.platforms.forEach((anEntry: { platform?: unknown; file?: unknown } | null) => {
			if (
				anEntry !== null &&
				typeof anEntry.platform === 'string' &&
				typeof anEntry.file === 'string' &&
				anEntry.file !== ''
			) {
				fileObj[anEntry.platform] = anEntry.file
			}
		})
	}

	return Object.keys(fileObj).length > 0
		? { status: 'ready', token, fileObj }
		: { status: 'unavailable' }
}

/**
 * 방문자의 기기. Download 밴드가 이 값으로 큰 버튼을 고른다.
 *
 * `arch` 의 `pending` 은 알아보는 중, `unknown` 은 끝내 알아내지 못했다는 뜻이다.
 * 크로뮴 계열은 물으면 답한다. Safari·Firefox 는 Apple silicon 에서도 자신을 Intel 이라고
 * 말하므로 UA 문자열로는 가르지 못하고, 그래픽 칩을 보고 가른다 (`readMacChipFromGpu`).
 */
interface VisitorType {
	os: 'mac' | 'win' | 'other'
	arch: 'arm64' | 'x64' | 'pending' | 'unknown'
}

interface UaDataType {
	platform?: string
	getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string }>
}

const readUaData = (): UaDataType | undefined =>
	typeof navigator === 'undefined' ? undefined : (navigator as any).userAgentData

/**
 * Mac 의 칩을 그래픽 칩으로 가른다. UA 가 입을 닫는 Safari·Firefox 를 위한 길이다.
 *
 * 2026-10-01 이 Mac(Apple silicon)의 실제 브라우저로 잰 값:
 * - Firefox 154 — 렌더러 이름이 `Apple M1, or similar`. Intel Mac 은 `Intel(R) …`·`Radeon …` 으로 나온다.
 * - Safari 26 — 렌더러 이름을 `Apple GPU` 로 가린다. 대신 압축 텍스처 확장이 갈린다:
 *   ASTC·PVRTC 는 Apple 이 만든 GPU 에만 있다(Metal 사양). Intel Mac 의 GPU 는 S3TC 만 안다.
 *
 * 🔴 옛 요령 「`s3tc_srgb` 가 없으면 Apple silicon」은 쓰지 않는다 — Safari 26 은 Apple silicon
 * 에서도 S3TC 를 낸다(실측). 그 요령대로면 전부 Intel 로 읽힌다.
 *
 * Intel 로 판정하는 길은 둘뿐이다: 렌더러 이름에 Intel·AMD 가 있거나, Safari 가 가린 이름 아래에서
 * S3TC 는 있는데 ASTC·PVRTC 가 없을 때. 그 밖은 `unknown` — 이름을 통째로 가리는 브라우저
 * (Firefox 의 지문 방지)에서 Apple silicon 을 Intel 로 읽지 않으려는 것이다.
 * 틀렸을 때의 값도 기울어 있다: Apple silicon 이 Intel 빌드를 받으면 Rosetta 로 돌지만,
 * Intel 이 Apple silicon 빌드를 받으면 열리지 않는다. 뒤쪽은 ASTC 가 있어야만 일어난다.
 */
const readMacChipFromGpu = (): 'arm64' | 'x64' | 'unknown' => {
	try {
		const gl = document.createElement('canvas').getContext('webgl')
		if (gl === null) {
			return 'unknown'
		}
		const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
		const renderer = String(
			(debugInfo !== null ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : null) ??
				gl.getParameter(gl.RENDERER) ??
				'',
		)
		const extensions = gl.getSupportedExtensions() ?? []
		gl.getExtension('WEBGL_lose_context')?.loseContext()

		if (/Apple M\d/i.test(renderer)) {
			return 'arm64'
		}
		if (/Intel|AMD|Radeon|NVIDIA|GeForce/i.test(renderer)) {
			return 'x64'
		}
		if (/Apple GPU/i.test(renderer)) {
			const has = (name: string) =>
				extensions.some(anExtension => anExtension.includes(name))
			if (has('compressed_texture_astc') || has('compressed_texture_pvrtc')) {
				return 'arm64'
			}
			if (has('compressed_texture_s3tc')) {
				return 'x64'
			}
		}
		return 'unknown'
	} catch (e) {
		return 'unknown'
	}
}

/**
 * 첫 렌더에서 바로 답한다 — OS 는 동기로 알 수 있다.
 *
 * 프리렌더는 `other` 다. 구운 HTML 은 크롤러가 읽는 정본이라 굽는 기계(Mac)의 버튼이 아니라
 * 플랫폼 전부를 담는다. 폰·태블릿도 `other` — 설치할 수 없는 기기에 큰 버튼을 세우지 않는다.
 * iPadOS 는 자신을 Mac 이라고 말하므로 터치 포인트로 가른다.
 */
const detectVisitor = (): VisitorType => {
	if (typeof window === 'undefined' || (window as any).__JUDAHS_PRERENDER__ === true) {
		return { os: 'other', arch: 'unknown' }
	}

	const uaData = readUaData()
	const platform = (uaData?.platform ?? navigator.platform ?? '').toLowerCase()
	const canAskArch = typeof uaData?.getHighEntropyValues === 'function'

	if (/iphone|ipad|ipod|android/i.test(navigator.userAgent)) {
		return { os: 'other', arch: 'unknown' }
	}
	if (platform.startsWith('mac')) {
		return navigator.maxTouchPoints > 1
			? { os: 'other', arch: 'unknown' }
			: { os: 'mac', arch: 'pending' }
	}
	if (platform.startsWith('win')) {
		return { os: 'win', arch: canAskArch ? 'pending' : 'unknown' }
	}
	return { os: 'other', arch: 'unknown' }
}

/**
 * 소프트웨어 상세 — `/software/{slug}` (PENDING_CHANGES §12). 매뉴얼에 인쇄되는 정식 주소.
 *
 * 설치파일 주소는 코드에 없다. S3 의 포인터(`latest.json`)를 클라이언트에서 읽어 조립하므로
 * 새 설치파일이 나와도 이 페이지는 고치지도 다시 배포하지도 않는다.
 * 포인터를 못 읽으면(설치파일을 갈아 끼우는 동안 잠깐 403) 링크를 감추고 한 줄만 남긴다 —
 * 페이지 자체는 언제나 살아 있다.
 *
 * Download 밴드는 방문자의 기기에 맞는 설치파일 하나를 블랙 CTA 로 세우고, 나머지는
 * 그 아래 「Also available for」 줄로 작게 둔다. 접지 않는다 — 플랫폼이 둘셋뿐이라 한 번 더
 * 누르게 할 이유가 없다. 기기를 모르면(폰·리눅스·프리렌더) 전부를 줄로 편다.
 */
const MonolithSoftwareDetailPage = () => {
	const navigate = useNavigate()
	const { slug } = useParams<{ slug: string }>()

	const software: SoftwareType | undefined = findSoftwareBySlug(slug ?? '')

	const [latest, setLatest] = React.useState<LatestStateType>({ status: 'loading' })
	const [visitor, setVisitor] = React.useState<VisitorType>(detectVisitor)

	// 칩 종류 — 크로뮴 계열은 물으면 답한다. 답이 없으면 Mac 은 그래픽 칩을 본다
	React.useEffect(() => {
		if (visitor.arch !== 'pending') {
			return
		}
		let cancelled = false
		const settle = (arch: VisitorType['arch']) => {
			if (!cancelled) setVisitor(prev => ({ ...prev, arch }))
		}
		const fallback = () => settle(visitor.os === 'mac' ? readMacChipFromGpu() : 'unknown')

		const uaData = readUaData()
		if (typeof uaData?.getHighEntropyValues !== 'function') {
			fallback()
			return
		}
		uaData
			.getHighEntropyValues(['architecture'])
			.then(res => {
				if (res.architecture === 'arm') settle('arm64')
				else if (res.architecture === 'x86') settle('x64')
				else fallback()
			})
			.catch(fallback)

		return () => {
			cancelled = true
		}
	}, [visitor.arch, visitor.os])

	React.useEffect(() => {
		if (software === undefined) {
			return
		}
		/*
			프리렌더(`scripts/prerender.js`)는 포인터를 읽지 않는다. 구운 HTML 에 그 시점의 파일
			주소가 박히면 다음 설치파일이 나오는 순간 틀린 링크가 된다. 플랫폼 줄만 굽고,
			링크는 브라우저에서 붙는다.
		*/
		if ((window as any).__JUDAHS_PRERENDER__ === true) {
			return
		}
		let cancelled = false

		fetch(software.latestUrl, { cache: 'no-store' })
			.then(res => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
			.then((json: InstallerPointerType) => {
				if (!cancelled) setLatest(readPointer(json))
			})
			.catch(() => {
				if (!cancelled) setLatest({ status: 'unavailable' })
			})

		return () => {
			cancelled = true
		}
	}, [software])

	if (software === undefined) {
		return <NotFoundPage />
	}

	const shots = shotsObj[software.code] ?? []

	// 구조화 데이터 — 화면에 있는 사실만 (이름·설명·요구 사양·기능 불릿·리드샷)
	const pagePath = `/software/${software.slug}`
	const jsonLd = [
		buildSoftwareApplicationJsonLd({
			name: software.name,
			description: `${software.tagline} ${software.description}`,
			path: pagePath,
			image:
				shots.length > 0 ? new URL(shots[0].src, `${SEO_SITE.origin}/`).href : undefined,
			operatingSystem: software.requirements
				.filter(
					aRequirement =>
						aRequirement.label === 'macOS' || aRequirement.label === 'Windows',
				)
				.map(aRequirement => `${aRequirement.label} ${aRequirement.value}`)
				.join(', '),
			featureList: software.features,
		}),
		buildBreadcrumbJsonLd([
			{ name: 'Home', path: '/' },
			{ name: 'Software', path: '/software' },
			{ name: software.name, path: pagePath },
		]),
	]

	/** 파일 주소 = `{downloadBaseUrl}/{경로 토큰}/{파일명}`. 뒤 둘은 포인터가 준 값 그대로다 */
	const buildUrl = (token: string, file: string) =>
		`${software.downloadBaseUrl}/${encodeURIComponent(token)}/${encodeURIComponent(file)}`

	/** 받을 수 있는 플랫폼. 포인터를 읽기 전에는 전부를 자리로 세우고, 읽은 뒤에는 포인터에 있는 것만 */
	const available: SoftwarePlatformType[] =
		latest.status === 'ready'
			? software.platforms.filter(aPlatform => latest.fileObj[aPlatform.id] !== undefined)
			: software.platforms

	/*
		큰 버튼에 올릴 것. 칩 종류까지 알고 그 빌드가 있으면 하나다. 칩을 끝내 모르면(WebGL 이 꺼진
		브라우저 등) 같은 OS 의 빌드를 나란히 세운다 — 틀린 칩의 설치파일은 열리지 않으므로 하나를 찍어 주지 않는다.
		묻는 중(`pending`)에는 자리 하나만 잡아 둔다. 답은 곧 오고, 버튼 수가 흔들리지 않는다.
	*/
	const sameOs = available.filter(aPlatform => aPlatform.os === visitor.os)
	const sameArch = sameOs.filter(aPlatform => aPlatform.arch === visitor.arch)
	const featured: SoftwarePlatformType[] =
		sameArch.length > 0
			? sameArch
			: visitor.arch === 'pending'
			? sameOs.slice(0, 1)
			: sameOs
	const others = available.filter(aPlatform => !featured.includes(aPlatform))
	const isSettling = latest.status !== 'ready' || visitor.arch === 'pending'

	const extOf = (aPlatform: SoftwarePlatformType): string =>
		latest.status === 'ready'
			? latest.fileObj[aPlatform.id].split('.').pop() ?? aPlatform.ext
			: aPlatform.ext

	/**
	 * 큰 버튼 아래 한 줄 — 어떤 기기용이고 무엇이 필요한가. 요구 사양은 Requirements 표에서
	 * 읽는다(같은 값을 두 번 적지 않는다). 버전은 적지 않는다.
	 */
	const noteOf = (aPlatform: SoftwarePlatformType): string => {
		const requirement = software.requirements.find(
			aRequirement => aRequirement.label === aPlatform.osName,
		)
		return [
			aPlatform.buildNote,
			requirement === undefined
				? undefined
				: `Requires ${aPlatform.osName} ${requirement.value}.`,
		]
			.filter(aPart => aPart !== undefined)
			.join(' ')
	}

	/** 링크가 되는 순간에만 붙는 속성. 그 전에는 같은 모양의 자리만 선다 */
	const linkPropsOf = (aPlatform: SoftwarePlatformType) =>
		latest.status === 'ready'
			? {
					href: buildUrl(latest.token, latest.fileObj[aPlatform.id]),
					onClick: () =>
						Analytics.download('download_software', {
							software_slug: software.slug,
							software_platform: aPlatform.id,
							// 집계용이다. 설치파일의 경로 토큰(리비전) — 화면에는 나가지 않는다
							software_version: latest.token,
						}),
			  }
			: undefined

	/** 헤어라인 줄 하나. 「Also available for」 아래와, 기기를 모를 때의 전체 목록이 같은 줄을 쓴다 */
	const renderRow = (aPlatform: SoftwarePlatformType) => {
		const linkProps = linkPropsOf(aPlatform)
		const inner = (
			<>
				<span className="mnDownloadLabel">{aPlatform.label}</span>
				<span className="mnDownloadExt">{`.${extOf(aPlatform)}`}</span>
			</>
		)

		return linkProps === undefined ? (
			<div key={aPlatform.id} className="mnDownload mnDownloadIdle">
				{inner}
			</div>
		) : (
			<a key={aPlatform.id} className="mnDownload" {...linkProps}>
				{inner}
			</a>
		)
	}

	return (
		<View css={style}>
			<Seo
				title={`${software.name} — Download | Judahs`}
				description={`${software.tagline} ${software.description}`}
				jsonLd={jsonLd}
			/>

			<View className="mnWrap">
				{/* header */}
				<View className="mnPageHeader">
					<View>
						<button
							type="button"
							className="mnBack"
							onClick={() => navigate('/software')}
						>
							← Software
						</button>
						<h1 className="mnPageTitle">{software.name}</h1>
					</View>
					<p className="mnPageLede">{software.tagline}</p>
				</View>

				{/* lead shot */}
				{shots.length > 0 && (
					<View className="mnLeadShot">
						<img src={shots[0].src} alt={software.name} className="mnLeadShotImage" />
					</View>
				)}

				{/* download */}
				<View className="mnBand mnSplit">
					<View className="mnSplitHead">
						<span className="mnKicker">Download</span>
					</View>
					<View className="mnSplitBody">
						{latest.status === 'unavailable' ? (
							/*
								포인터를 못 읽었다. 눌러도 받을 수 없는 줄을 세워 두지 않는다 —
								줄을 감추고 일어난 사실만 적는다. 기다리라는 말은 쓰지 않는다.
							*/
							<p className="mnBody mnMuted">
								The download links did not load. Please reload the page.
							</p>
						) : (
							<>
								<p className="mnDownloadLead">{software.downloadLead}</p>

								{featured.length > 0 ? (
									<>
										{/* 방문자의 기기에 맞는 설치파일. 이 페이지의 유일한 블랙 CTA 다 */}
										<View className="mnDownloadPrimary">
											<View className="mnDownloadActions">
												{featured.map((aPlatform, idx) => {
													const linkProps = isSettling
														? undefined
														: linkPropsOf(aPlatform)
													const className = idx === 0 ? 'mnCta' : 'mnCtaGhost'
													// 버튼에는 언제나 OS 이름이 들어간다. 나란히 서면 괄호로 빌드를 가른다
													const label =
														featured.length === 1
															? `Download for ${aPlatform.osName}`
															: `Download for ${aPlatform.osName} (${aPlatform.buildName})`

													return linkProps === undefined ? (
														<span
															key={aPlatform.id}
															className={`${className} mnDownloadCtaIdle`}
															aria-disabled="true"
														>
															{label}
														</span>
													) : (
														<a key={aPlatform.id} className={className} {...linkProps}>
															{label}
														</a>
													)
												})}
											</View>
											<p className="mnDownloadHint">
												{featured.length === 1
													? noteOf(featured[0])
													: software.buildHintObj[featured[0].os] ?? noteOf(featured[0])}
											</p>
										</View>

										{/* 나머지 플랫폼 — 같은 자리 아래에 작게. 다른 기기에 깔려는 사람이 찾는 줄이다 */}
										{others.length > 0 && (
											<View className="mnDownloadOthers">
												<span className="mnDownloadOthersLabel">Also available for</span>
												<View className="mnDownloads">{others.map(renderRow)}</View>
											</View>
										)}
									</>
								) : (
									/* 기기를 모른다(폰·리눅스·프리렌더). 찍어 줄 것이 없으니 전부를 줄로 편다 */
									<View className="mnDownloads">{available.map(renderRow)}</View>
								)}
							</>
						)}
					</View>
				</View>

				{/* about — 한 줄에 하나씩. 문단으로 묶으면 무엇이 되는지가 흐려진다 */}
				<View className="mnBand mnSplit">
					<View className="mnSplitHead">
						<span className="mnKicker">What it does</span>
					</View>
					<View className="mnSplitBody">
						<ul className="mnFeatures">
							{software.features.map(aFeature => (
								<li key={aFeature} className="mnFeature">
									{aFeature}
								</li>
							))}
						</ul>
					</View>
				</View>

				{/* screens */}
				{shots.length > 1 && (
					<View className="mnBand">
						<span className="mnKicker mnSectionKicker">On screen</span>
						<View className="mnShots">
							{shots.slice(1).map(aShot => (
								<View key={aShot.caption} className="mnShot">
									<img src={aShot.src} alt="" className="mnShotImage" />
									<div className="mnShotCaption">{aShot.caption}</div>
								</View>
							))}
						</View>
					</View>
				)}

				{/* requirements */}
				<View className="mnBand mnSplit mnLast">
					<View className="mnSplitHead">
						<span className="mnKicker">Requirements</span>
					</View>
					<View className="mnSplitBody">
						<View className="mnSpecTable">
							{software.requirements.map(aReq => (
								<View key={aReq.label} className="mnSpecRow">
									<span className="mnSpecLabel">{aReq.label}</span>
									<span className="mnSpecValue">{aReq.value}</span>
								</View>
							))}
						</View>
					</View>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithSoftwareDetailPage

const style = css({
	...monoShared,
	position: 'relative',
	width: '100%',

	// 페이지 헤더 — 1열. 제목은 전폭, 소제목은 그 아래(DESIGN_SYSTEM §6.2). 4:8 은 두 단어 제목(Works with)이 줄바꿈돼 폐기.
	'.mnPageHeader': {
		padding: 'clamp(56px, 7vw, 100px) 0 clamp(40px, 5vw, 72px)',

		'.mnBack': {
			display: 'inline-block',
			marginBottom: 28,
			padding: 0,
			border: 0,
			background: 'transparent',
			fontFamily: 'inherit',
			fontSize: 13,
			fontWeight: 500,
			color: mono.faint,
			cursor: 'pointer',
			transition: 'color 200ms ease-out',
			'&:hover': { color: mono.ink },
		},
		'.mnPageTitle': {
			fontSize: 'clamp(40px, 5.5vw, 80px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},
		'.mnPageLede': {
			fontSize: 19,
			lineHeight: 1.6,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 520,
			marginTop: 'clamp(20px, 2vw, 28px)',
		},
	},

	'.mnLeadShot': {
		marginBottom: 'clamp(48px, 6vw, 90px)',

		'.mnLeadShotImage': {
			width: '100%',
			display: 'block',
			border: `1px solid ${mono.line}`,
			borderRadius: 8,
			boxShadow: '0 30px 60px rgba(20,24,32,0.08)',
		},
	},

	// 키커(4) : 본문(8) 분할 밴드
	'.mnSplit': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 4fr) minmax(0, 8fr)',
		gap: 48,
		padding: 'clamp(48px, 5.5vw, 80px) 0',

		'.mnSplitHead': {
			display: 'flex',
			alignItems: 'baseline',
			gap: 20,
		},
		'.mnSplitBody': {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: 24,
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 20,
		},
	},

	'.mnLast': {
		marginBottom: 'clamp(64px, 8.3vw, 120px)',
	},

	'.mnBody': {
		fontSize: 17,
		lineHeight: 1.7,
		fontWeight: 300,
		color: mono.dim,
		maxWidth: 620,
	},
	'.mnMuted': {
		color: mono.faint,
	},

	'.mnDownloads': {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		maxWidth: 560,
		borderTop: `1px solid ${mono.line}`,

		'.mnDownload': {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			width: '100%',
			padding: '18px 0',
			borderBottom: `1px solid ${mono.line}`,
			color: mono.ink,
			cursor: 'pointer',
			transition: 'padding-left 200ms ease-out',

			'&:hover': {
				paddingLeft: 8,
			},
			'&:focus-visible': {
				outline: `1px solid ${mono.ink}`,
				outlineOffset: 2,
			},

			/* 포인터를 읽기 전의 줄. 자리는 지키되 눌리지 않고 움직이지도 않는다 */
			'&.mnDownloadIdle': {
				color: mono.faint,
				cursor: 'default',
				'&:hover': { paddingLeft: 0 },
			},

			'.mnDownloadLabel': {
				fontSize: 16,
				fontWeight: 400,
			},
			'.mnDownloadExt': {
				fontSize: 12,
				letterSpacing: '.18em',
				textTransform: 'uppercase',
				color: mono.faint,
				fontWeight: 500,
			},
		},
	},

	/*
		기능 목록. 브라우저 기본 불릿(disc)은 크기가 서체와 따로 놀아서 쓰지 않고,
		같은 자리에 작은 사각 마커를 직접 놓는다 (Monolith 는 radius 0 이다).
	*/
	'.mnFeatures': {
		listStyle: 'none',
		margin: 0,
		padding: 0,
		maxWidth: 620,

		'.mnFeature': {
			position: 'relative',
			paddingLeft: 20,
			fontSize: 16,
			fontWeight: 300,
			lineHeight: 1.7,
			color: mono.ink,

			'& + .mnFeature': {
				marginTop: 10,
			},

			'&::before': {
				content: '""',
				position: 'absolute',
				left: 0,
				top: '0.62em',
				width: 5,
				height: 5,
				background: mono.faint,
			},
		},
	},

	/*
		밴드의 첫 줄 — 받을 수 있고 값이 없다는 사실. 버튼보다 먼저, 크게 읽힌다.
		제목 계열(clamp 40~80)보다는 확실히 작게 잡아 페이지 제목과 경쟁하지 않는다.
	*/
	'.mnDownloadLead': {
		fontSize: 'clamp(24px, 2.6vw, 34px)',
		lineHeight: 1.2,
		fontWeight: 200,
		letterSpacing: '-0.01em',
		color: mono.ink,
		// 데스크톱에서는 한 줄. 줄이 나뉠 때는 마지막 단어 하나만 떨어지지 않게 고르게 나눈다
		maxWidth: 760,
		textWrap: 'balance',
		marginBottom: 8,
	},

	/* 큰 버튼과 그 아래 한 줄을 한 덩어리로 묶는다 */
	'.mnDownloadPrimary': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: 14,
	},
	'.mnDownloadActions': {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 12,

		/* 나란히 서는 두 버튼은 높이가 같다 (고스트 기본은 50) */
		'.mnCtaGhost': {
			height: 54,
		},
		'.mnCta, .mnCtaGhost': {
			transition: 'background-color 200ms ease-out',
			'&:focus-visible': {
				outline: `1px solid ${mono.ink}`,
				outlineOffset: 3,
			},
		},

		/* 링크가 붙기 전의 자리. 모양과 크기는 같고 눌리지 않는다 */
		'.mnDownloadCtaIdle': {
			opacity: 0.35,
			cursor: 'default',
		},

		/* 폰에서는 버튼이 폭을 채운다 — 글자가 긴 쪽이 잘리지 않는다 */
		'@media (max-width: 600px)': {
			width: '100%',
			'.mnCta, .mnCtaGhost': {
				flex: '1 1 100%',
				padding: '0 20px',
			},
		},
	},
	/* 버튼 아래 한 줄 — 어떤 기기용인지, 또는 두 버튼 중 어느 쪽인지 가르는 법 */
	'.mnDownloadHint': {
		fontSize: 14,
		lineHeight: 1.6,
		fontWeight: 300,
		color: mono.dim,
		maxWidth: 520,
	},

	/* 나머지 플랫폼. 작은 라벨 하나와 헤어라인 줄 — 블랙 CTA 보다 한참 낮은 톤이다 */
	'.mnDownloadOthers': {
		display: 'flex',
		flexDirection: 'column',
		gap: 14,
		width: '100%',
		maxWidth: 560,
		marginTop: 20,
	},
	'.mnDownloadOthersLabel': {
		fontSize: 12,
		letterSpacing: '.18em',
		textTransform: 'uppercase',
		color: mono.faint,
		fontWeight: 500,
	},

	'@media (prefers-reduced-motion: reduce)': {
		'.mnDownload, .mnCta, .mnCtaGhost': {
			transition: 'none',
		},
	},

	'.mnSectionKicker': {
		display: 'block',
		marginBottom: 48,
	},

	'.mnShots': {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: '48px 40px',

		'.mnShot': {
			'.mnShotImage': {
				width: '100%',
				display: 'block',
				border: `1px solid ${mono.line}`,
				borderRadius: 6,
			},
			'.mnShotCaption': {
				fontSize: 13,
				fontWeight: 300,
				color: mono.faint,
				marginTop: 12,
			},
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
		},
	},

	'.mnSpecTable': {
		width: '100%',
		maxWidth: 620,

		'.mnSpecRow': {
			display: 'grid',
			gridTemplateColumns: 'minmax(0, 200px) minmax(0, 1fr)',
			gap: 24,
			padding: '15px 0',
			borderBottom: `1px solid ${mono.line}`,
			alignItems: 'baseline',

			'.mnSpecLabel': {
				fontSize: 13,
				letterSpacing: '.14em',
				textTransform: 'uppercase',
				color: mono.faint,
				fontWeight: 500,
			},
			'.mnSpecValue': {
				fontSize: 16,
				color: mono.ink,
				lineHeight: 1.5,
				fontWeight: 300,
			},
		},
	},
})
